import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const CitiesPage = () => {
  const cities = [
    {
      id: 'sao-paulo',
      name: 'São Paulo',
      tagline: 'The Pulse of South America',
      description: 'Twenty-two million people. The financial capital of Latin America. A city that never reveals its best secrets to outsiders. São Paulo rewards those who know where to look — and more importantly, who to know. Our network runs deep here: from the private dining rooms of Jardins to the rooftops that don\'t appear on any map.',
      highlights: [
        'Michelin-quality private dining',
        'Rooftops without reservations',
        'Art world insider access',
        'After-hours that matter'
      ],
      mainImage: 'https://images.unsplash.com/photo-1583788758158-7a28a8c989ae?auto=format&fit=crop&w=1200&q=80',
      secondaryImage: 'https://images.unsplash.com/photo-1606761568499-6d2451b23c66?auto=format&fit=crop&w=600&q=80'
    },
    {
      id: 'rio-de-janeiro',
      name: 'Rio de Janeiro',
      tagline: 'Where Beauty Commands Attention',
      description: 'Rio seduces everyone who visits. But the Rio that tourists see is not the Rio we know. Behind the postcard views lie private beaches, hilltop villas invisible from below, helicopter routes that reveal the city\'s true scale. We\'ve built relationships here over years — with yacht captains, property owners, and the people who make Rio\'s most exclusive moments possible.',
      highlights: [
        'Private helicopter routes',
        'Hidden beach access',
        'Yacht connections',
        'Carnival inner circle'
      ],
      mainImage: 'https://images.unsplash.com/photo-1567710726191-c0b9b167239a?auto=format&fit=crop&w=1200&q=80',
      secondaryImage: 'https://images.unsplash.com/photo-1518639192441-8fce0a366e2e?auto=format&fit=crop&w=600&q=80'
    }
  ];

  return (
    <div data-testid="cities-page" className="min-h-screen pt-24 md:pt-32">
      {/* Page Header */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto">
          <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
            Where We Operate
          </p>
          <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-[#F5F5F0] mb-8 leading-[1.1]">
            Two Cities. <span className="italic">Decades of Connections.</span>
          </h1>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-2xl font-manrope font-light leading-relaxed">
            We operate exclusively where our network runs deepest. São Paulo and Rio de Janeiro 
            — two cities where the right introduction opens doors that money alone cannot.
          </p>
        </div>
      </section>

      {/* City Sections */}
      {cities.map((city, index) => (
        <section
          key={city.id}
          data-testid={`city-section-${city.id}`}
          className={`py-20 md:py-28 px-6 md:px-12 lg:px-24 ${
            index % 2 === 0 ? 'bg-obsidian' : 'bg-obsidian-paper'
          }`}
        >
          <div className="max-w-[1400px] mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 ${
              index % 2 !== 0 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content */}
              <div className={`flex flex-col justify-center ${index % 2 !== 0 ? 'lg:order-2' : ''}`}>
                <p className="text-gold text-xs uppercase tracking-[0.4em] mb-5 font-manrope font-medium">
                  {city.tagline}
                </p>
                <h2 className="font-playfair text-4xl md:text-5xl text-[#F5F5F0] mb-8">
                  {city.name}
                </h2>
                <p className="text-[#A1A1AA] text-base leading-[1.9] font-manrope font-light mb-10">
                  {city.description}
                </p>
                
                {/* Highlights */}
                <div className="grid grid-cols-2 gap-y-5 gap-x-8 mb-10">
                  {city.highlights.map((highlight, hIndex) => (
                    <div key={hIndex} className="flex items-center gap-4">
                      <div className="w-1.5 h-1.5 bg-gold/70" />
                      <span className="text-[#F5F5F0] text-sm font-manrope font-light">
                        {highlight}
                      </span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/contact"
                  data-testid={`city-cta-${city.id}`}
                  className="inline-flex items-center gap-3 text-gold text-xs uppercase tracking-[0.2em] font-medium hover:text-gold-light transition-colors underline-hover w-fit"
                >
                  Plan Your {city.name.split(' ')[0]} Experience
                  <ArrowRight size={14} />
                </Link>
              </div>

              {/* Images */}
              <div className={`relative ${index % 2 !== 0 ? 'lg:order-1' : ''}`}>
                <div className="relative h-[500px] lg:h-[600px] overflow-hidden border border-white/5">
                  <img
                    src={city.mainImage}
                    alt={city.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                </div>
                {/* Secondary Image */}
                <div className="absolute -bottom-8 -right-8 w-48 h-48 md:w-64 md:h-64 border border-gold/20 overflow-hidden hidden md:block">
                  <img
                    src={city.secondaryImage}
                    alt={`${city.name} detail`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="py-28 md:py-36 px-6 md:px-12 lg:px-24 bg-obsidian">
        <div className="max-w-[1400px] mx-auto text-center">
          <p className="text-gold text-xs uppercase tracking-[0.4em] mb-8 font-manrope font-medium">
            Your Brazil Awaits
          </p>
          <h2 className="font-playfair text-3xl md:text-4xl text-[#F5F5F0] mb-8">
            Which city calls to <span className="italic">you</span>?
          </h2>
          <p className="text-[#A1A1AA] text-base md:text-lg max-w-xl mx-auto mb-12 font-manrope font-light leading-relaxed">
            Whether it's São Paulo's urban intensity or Rio's natural drama — 
            we're ready to show you the Brazil you could never access alone.
          </p>
          <Link
            to="/contact"
            data-testid="cities-main-cta"
            className="inline-flex items-center gap-3 bg-gold text-black px-10 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold-light transition-all duration-500 luxury-btn"
          >
            Begin the Conversation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CitiesPage;
